import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socket';
import '../styles/PairCountSelection.css';

interface Room {
  id: number;
  roomCode: string;
  pairCount: number;
  maxPlayers: number;
  dictionaryData: Record<string, string>;
  dictionaryName: string;
}

interface PairCountSelectionProps {
  room: Room;
  isHost: boolean;
  maxPairs: number;
  onComplete: (selectedPairCount: number) => void;
}

const PairCountSelection: React.FC<PairCountSelectionProps> = ({
  room,
  isHost,
  maxPairs: maxPairsProp,
  onComplete
}) => {
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(5);
  const [selectedPairCount, setSelectedPairCount] = useState<number | null>(null);

  // Ensure maxPairs is a number to prevent string concatenation
  const maxPairs = Number(maxPairsProp);

  // Countdown timer - ONLY for host
  useEffect(() => {
    if (!isHost) return; // Non-host doesn't run countdown

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Time's up - use previous pair count or selected count
          const finalPairCount = selectedPairCount || room.pairCount;

          if (user) {
            // Emit the final pair count selection with dictionary data
            socketService.getSocket()?.emit('pair_count_selected', {
              roomId: room.id,
              pairCount: finalPairCount,
              dictionaryData: room.dictionaryData,
              dictionaryName: room.dictionaryName
            });
          }

          // Wait for countdown to finish before proceeding
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPairCount, room.pairCount, room.id, isHost, user]);

  // Auto-proceed after countdown reaches 0 - ONLY for host
  useEffect(() => {
    if (isHost && countdown === 0) {
      const finalPairCount = selectedPairCount || room.pairCount;
      setTimeout(() => {
        onComplete(finalPairCount);
      }, 500); // Small delay after countdown finishes
    }
  }, [isHost, countdown, selectedPairCount, room.pairCount, onComplete]);

  // Listen for pair count selection from host
  useEffect(() => {
    if (!user) return;

    const handlePairCountSelected = (data: { pairCount: number }) => {
      console.log('🎯 Pair count selected:', data.pairCount);
      // Non-host players receive the selection
      if (!isHost) {
        onComplete(data.pairCount);
      }
    };

    socketService.on('pair_count_selected', handlePairCountSelected);

    return () => {
      socketService.off('pair_count_selected', handlePairCountSelected);
    };
  }, [user, isHost, onComplete]);

  const handlePairCountChange = (count: number) => {
    if (isHost) {
      setSelectedPairCount(count);
    }
  };

  return (
    <div className="pair-count-overlay">
      <div className="pair-count-popup">
        {isHost ? (
          <>
            <h2>⚙️ Configure Game</h2>
            <p className="countdown-display">⏱ {countdown} seconds remaining</p>
            <p className="instruction">Select number of matches for this round</p>

            <div className="pair-count-slider">
              <label>
                <span className="slider-label">Matches: {selectedPairCount || room.pairCount}</span>
                <input
                  type="range"
                  min="3"
                  max={maxPairs}
                  value={selectedPairCount || room.pairCount}
                  onChange={(e) => handlePairCountChange(parseInt(e.target.value))}
                />
              </label>
            </div>

            <p className="auto-select-notice">
              {selectedPairCount
                ? `Will use ${selectedPairCount} matches when countdown finishes`
                : `Will use ${room.pairCount} matches (same as previous round) if no selection`
              }
            </p>
          </>
        ) : (
          <>
            <h2>⏳ Waiting for Host</h2>
            <p className="instruction">The host is selecting the number of matches...</p>

            <div className="waiting-animation">
              <div className="spinner"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PairCountSelection;
